<template>
  <div class="p-24">
    <div
      class="bg-static-default-low mood-tangaroa rounded-lg relative flex w-full grow flex-col overflow-hidden p-20"
    >
      <IconBackMarket class="text-static-default-hi mx-auto mb-16 size-32" />
      <div class="mood-main">
        <h3 class="text-static-light-hi heading-3 mb-16 text-center">
          {{ i18n(translations.checkpointsTitle) }}
          <br />
          {{ i18n(translations.checkpointsSubTitle) }}
        </h3>
        <p class="text-static-light-hi body-2 mb-16">
          {{ i18n(translations.checkpointsDescription) }}
        </p>
        <section
          v-if="!isEmpty(categories)"
          class="flex h-[90vh] max-h-[650px] w-full justify-center pt-16"
        >
          <BaseAnimatedProductCheckpoints
            :categories
            class="h-full w-full max-w-[1440px]"
            :i18n
            :trackClick
            trackingZone="product modal"
            variant="row"
          />
        </section>
      </div>
    </div>
    <div class="my-32">
      <h3 class="heading-2 mb-32 text-center">
        {{ i18n(translations.differenceVsUsedTitle) }}
      </h3>
      <p class="body-2">
        {{ i18n(translations.differenceVsUsedDescription) }}
      </p>
    </div>
    <div class="mb-32 grid grid-cols-2 gap-x-12">
      <VersusTable
        :icon="IconScratches"
        :title="i18n(translations.vsUsedTitle)"
      >
        <VersusTableItem>{{ i18n(translations.vsUsedState) }}</VersusTableItem>
        <VersusTableItem>{{
          i18n(translations.vsUsedRepairState)
        }}</VersusTableItem>
        <VersusTableItem>{{
          i18n(translations.vsUsedWarranty)
        }}</VersusTableItem>
        <VersusTableItem>{{
          i18n(translations.vsUsedBattery)
        }}</VersusTableItem>
      </VersusTable>
      <VersusTable
        :icon="IconCheckWithSparkles"
        :title="i18n(translations.vsRefurbishedTitle)"
      >
        <VersusTableItem emphasis>{{
          i18n(translations.vsRefurbishedState)
        }}</VersusTableItem>
        <VersusTableItem emphasis>{{
          i18n(translations.vsRefurbishedRepairState)
        }}</VersusTableItem>
        <VersusTableItem emphasis>{{
          i18n(translations.vsRefurbishedWarranty)
        }}</VersusTableItem>
        <VersusTableItem emphasis>{{
          i18n(translations.vsRefurbishedBattery)
        }}</VersusTableItem>
      </VersusTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import BaseAnimatedProductCheckpoints from '@backmarket/nuxt-layer-cms/BaseAnimatedProductCheckpoints.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { IconBackMarket } from '@ds/icons/IconBackMarket'
import { IconCheckWithSparkles } from '@ds/icons/IconCheckWithSparkles'
import { IconScratches } from '@ds/icons/IconScratches'

import { CATEGORIES_MAP } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ConditionDescriptionModal/components/Checkpoints/Checkpoints.constants'

import translations from '../../VerifiedRefurbishedModal.translations'
import VersusTable from '../VersusTable.vue'
import VersusTableItem from '../VersusTableItem.vue'

interface VerifiedRefurbishedModalBodyProps {
  categoryId?: string
}

const props = defineProps<VerifiedRefurbishedModalBodyProps>()

const i18n = useI18n()
const route = useRoute()
const { trackClick } = useTracking()

// [Apps]`categoryId` is passed as a prop when rendered inside the drawer, but comes
// from the URL query when this body is rendered standalone as a webview route.
const categoryId = computed(
  () => props.categoryId ?? (route.query.categoryId as string | undefined),
)

const categories = computed(() => {
  return categoryId.value && CATEGORIES_MAP[categoryId.value]
    ? [CATEGORIES_MAP[categoryId.value]]
    : []
})
</script>
